import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { apiPost } from '../config/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [fbUser, setFbUser] = useState(null);
  const [student, setStudent] = useState(null);
  const [authState, setAuthState] = useState('loading');
  // loading | checking | unauthenticated | not_found | registered | needs_onboarding | found
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      setAuthError('');
      if (!user) {
        setFbUser(null);
        setStudent(null);
        setAuthState('unauthenticated');
        return;
      }
      setFbUser(user);
      setAuthState('checking');
      try {
        const res = await apiPost('/student-portal/check', user);
        const s = res.data?.data?.student;
        setStudent(s);
        // If course is still default, student needs onboarding
        if (s?.course === 'Pending' && s?.country === 'TBD') {
          setAuthState('needs_onboarding');
        } else {
          setAuthState('found');
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setAuthState('not_found');
        } else {
          setAuthError(err.response?.data?.message || 'Something went wrong.');
          await signOut(auth);
          setAuthState('unauthenticated');
        }
      }
    });
  }, []);

  const signIn = async () => {
    try {
      setAuthError('');
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setAuthError('Sign in failed. Please try again.');
      }
    }
  };

  const signOutUser = async () => {
    await signOut(auth);
    setStudent(null);
    setAuthState('unauthenticated');
  };

  const register = async (name, phone) => {
    const res = await apiPost('/student-portal/register', fbUser, { name, phone });
    return res.data;
  };

  const markRegistered = () => setAuthState('registered');

  const completeOnboarding = async (data) => {
    const res = await apiPost('/student-portal/onboarding', fbUser, data);
    setStudent(res.data?.data?.student);
    setAuthState('found');
  };

  const skipOnboarding = () => {
    setAuthState('found');
  };

  const refreshStudent = async () => {
    if (!fbUser) return;
    try {
      const res = await apiPost('/student-portal/check', fbUser);
      setStudent(res.data?.data?.student);
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{
      fbUser, student, authState, authError, setAuthError,
      signIn, signOutUser, register, markRegistered,
      completeOnboarding, skipOnboarding, refreshStudent,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
